/**
 * Rules of the single-measurement edition's study setup.
 *
 * FaceWise runs one measurement: one study, one material, a fixed metric. The
 * length cap lives here rather than inside the screen because two places need
 * the same answer — the material card, which explains why a video was refused,
 * and the wizard footer, which must not offer "Launch" for a study the backend
 * would reject.
 */
import type { StimulusDraft, StudyDraft } from './types'

/** Longest material a FaceWise Basic study may show. Mirrors the backend cap
 *  (`BASIC_MAX_STIMULUS_MINUTES`); Pro's is higher and enforced server-side. */
export const MAX_STIMULUS_MINUTES = 5
export const MAX_STIMULUS_SECONDS = MAX_STIMULUS_MINUTES * 60

/** How long an image or a text card is shown when nobody sets an exposure —
 *  FaceWise has no exposure field, so this is the exposure. */
export const DEFAULT_EXPOSURE_MS = 5000

/** True once we know the material runs longer than the cap. An unmeasured
 *  material (a codec the browser won't decode, a link we could not read) is
 *  not "too long": the upload endpoint has the final say, and refusing here on
 *  a guess would block legitimate studies. */
export function stimulusTooLong(stimulus: StimulusDraft | undefined): boolean {
  return stimulus?.durationSeconds != null && stimulus.durationSeconds > MAX_STIMULUS_SECONDS
}

/** The one stimulus a FaceWise study carries, if it has been picked yet. */
export function soleStimulus(draft: StudyDraft): StimulusDraft | undefined {
  return draft.stimuli[0]
}

/** A material the respondent can actually be shown: a file, a link, or text. */
function stimulusIsComplete(stimulus: StimulusDraft): boolean {
  if (stimulus.type === 'url') return !!stimulus.url?.trim()
  if (stimulus.type === 'text') return !!stimulus.payload?.trim()
  return !!stimulus.file || stimulus.serverId != null
}

/** Everything the study needs before it can go live. */
export function basicReadyToLaunch(draft: StudyDraft): boolean {
  const stimulus = soleStimulus(draft)
  return (
    draft.name.trim().length > 0 &&
    stimulus !== undefined &&
    stimulusIsComplete(stimulus) &&
    !stimulusTooLong(stimulus)
  )
}

/** Length of a picked video file, read from the browser's own decoder. Returns
 *  null when the metadata cannot be read — see `stimulusTooLong`. */
export function videoFileDurationSeconds(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const video = document.createElement('video')
    let done = false
    const finish = (seconds: number | null) => {
      if (done) return
      done = true
      window.clearTimeout(timer)
      video.removeAttribute('src')
      URL.revokeObjectURL(url)
      resolve(seconds)
    }
    // A file the decoder chews on forever must not hang the picker.
    const timer = window.setTimeout(() => finish(null), 15000)
    video.preload = 'metadata'
    video.muted = true
    video.onloadedmetadata = () =>
      finish(Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null)
    video.onerror = () => finish(null)
    video.src = url
  })
}

/** Seconds as m:ss (or h:mm:ss), for showing a material's length. */
export function formatClock(seconds: number): string {
  const total = Math.round(seconds)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m)
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`
}
